import { useEffect, useState } from "react";
import { formatUnits } from "ethers";
import { formatContractError } from "@/lib/error";
import { useContracts } from "@/hooks/useContracts";
import { useUserCats } from "@/hooks/useUserCats";
import { useCatclawAllowance } from "@/hooks/useCatclawAllowance";
import { useCatclawBalance } from "@/hooks/useCatclawBalance";
import { ADDRESSES, getProvider } from "@/lib/contracts";
import { getFuseBurnCosts } from "@/lib/chainConstants";
import { Button } from "@/components/ui/button";
import { Loader2, Combine } from "lucide-react";
import { cn } from "@/lib/utils";
import { refreshAfterTx } from "@/lib/refreshAll";

interface FusePanelProps {
  tokenId?: number | null;
  onDone?: () => void;
}

export function FusePanel({ tokenId, onDone }: FusePanelProps) {
  const { nft, account, connected, requestConnect } = useContracts();
  const { cats } = useUserCats();
  const { allowance, approve, refresh: refreshAllowance } =
    useCatclawAllowance(ADDRESSES.CatStonks);
  const { balance, decimals } = useCatclawBalance(account);
  const [selected, setSelected] = useState<number[]>([]);
  const [costs, setCosts] = useState<{ two: bigint; three: bigint }>({ two: 0n, three: 0n });
  const [isPending, setIsPending] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFuseBurnCosts().then(setCosts);
  }, []);

  // 切换猫时清空已选的被吸收猫
  useEffect(() => {
    setSelected([]);
  }, [tokenId]);

  if (!connected) {
    return (
      <Button variant="pixel" size="pixel" className="w-full" onClick={requestConnect}>
        Connect Wallet
      </Button>
    );
  }

  if (!tokenId) {
    return <p className="text-sm text-muted-foreground">Select a survivor cat above to start fusing.</p>;
  }

  const survivor = tokenId;
  const absorbableCats = cats.filter((c) => c.tokenId !== survivor);
  const canFuse = absorbableCats.length >= 1;

  const toggleCat = (id: number) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      // 只支持 2 猫融合：当前猫 + 1 只被吸收猫
      return [id];
    });
  };

  const absorbedCount = selected.length;
  const totalCount = 1 + absorbedCount;
  const isValid = absorbedCount === 1;
  const cost = absorbedCount === 1 ? costs.two : 0n;
  const needsApprove = cost > 0n && allowance < cost;
  const hasEnoughCatclaw = cost > 0n && balance >= cost;

  const handleFuse = async () => {
    if (!isValid) return;
    setError(null);

    if (!hasEnoughCatclaw) {
      setError(`Insufficient $CATCLAW balance. Need ${formatUnits(cost, decimals)}, have ${formatUnits(balance, decimals)}.`);
      return;
    }

    setIsPending(true);
    try {
      const fuseArgs = [survivor, ...selected];

      // 先静态调用拿到明确的 revert reason
      try {
        await nft.fuse.staticCall(fuseArgs);
      } catch (staticErr) {
        setError(formatContractError(staticErr));
        setIsPending(false);
        return;
      }

      if (needsApprove) {
        setStep("Approve");
        await approve();
        await refreshAllowance();
      }

      setStep("Fuse");
      const provider = getProvider();

      // 自定义 gas 估算，避免钱包给出异常 gas limit
      const data = nft.interface.encodeFunctionData("fuse", [fuseArgs]);
      const gasEstimate = await provider.estimateGas({
        to: nft.target as string,
        from: account ?? undefined,
        data,
      });
      const gasLimit = (gasEstimate * 120n) / 100n;
      const maxFeePerGas = 100n * 10n ** 9n;
      const maxPriorityFeePerGas = 100n * 10n ** 9n;

      const tx = await nft.fuse(fuseArgs, { gasLimit, maxFeePerGas, maxPriorityFeePerGas });
      await provider.waitForTransaction(tx.hash, 1, 30000);
      setSelected([]);
      await refreshAllowance();
      await refreshAfterTx(tokenId);
      onDone?.();
    } catch (err) {
      setError(formatContractError(err));
    } finally {
      setIsPending(false);
      setStep(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-background p-4">
        <p className="font-pixel text-xs text-primary">SURVIVOR</p>
        <div className="mt-2 flex items-center gap-3">
          <img
            src={`/images/nfts-thumb/${survivor}.webp`}
            alt={`Cat #${survivor}`}
            className="h-16 w-16 rounded-lg object-cover"
          />
          <span className="font-pixel text-sm">Cat #{survivor}</span>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Select another cat to absorb into the survivor.
      </p>

      <div className="grid grid-cols-3 gap-2">
        {absorbableCats.map(({ tokenId }) => (
          <button
            key={tokenId}
            onClick={() => toggleCat(tokenId)}
            className={cn(
              "relative aspect-square overflow-hidden rounded-xl border transition-colors",
              selected.includes(tokenId)
                ? "border-primary ring-2 ring-primary"
                : "border-border hover:border-primary/50"
            )}
          >
            <img
              src={`/images/nfts-thumb/${tokenId}.webp`}
              alt={`Cat #${tokenId}`}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            {selected.includes(tokenId) && (
              <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
                {selected.indexOf(tokenId) + 1}
              </span>
            )}
          </button>
        ))}
      </div>

      {!canFuse && (
        <p className="text-sm text-muted-foreground">You need at least one other cat to fuse.</p>
      )}

      {isValid && cost > 0n && (
        <div className="rounded-xl border border-border bg-background p-4">
          <p className="font-pixel text-xs text-primary">COST</p>
          <p className="mt-1 font-mono text-sm">{formatUnits(cost, decimals)} $CATCLAW</p>
        </div>
      )}

      <Button
        variant="pixel"
        size="pixel"
        className="w-full"
        onClick={handleFuse}
        disabled={!isValid || isPending}
      >
        {isPending ? (
          <span className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {step ?? "Fusing"}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Combine size={16} />
            Fuse {totalCount} Cats
          </span>
        )}
      </Button>

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
